import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trophy, Target, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

function fmtTime(s) { if (s == null) return "—"; const m = Math.floor(s/60), sec = s%60; return `${m}m ${sec}s`; }

export default function AdminAttemptDetail() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/attempts/${attemptId}`);
        setDetail(data);
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); navigate("/admin/results", { replace: true }); }
      finally { setLoading(false); }
    })();
  }, [attemptId, navigate]);

  if (loading || !detail) return <AppLayout variant="admin"><div className="p-8 text-stone">Loading…</div></AppLayout>;
  const { attempt, student, exercise, review } = detail;

  return (
    <AppLayout variant="admin">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate(-1)} className="text-sm font-bold text-stone hover:text-ink mb-4">← Back</button>
        <div className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink">{exercise.title}</h1>
          <p className="text-stone mt-1">{student.name} · {new Date(attempt.submitted_at).toLocaleString()}</p>
        </div>

        <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-8">
          <div className="p-5 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble">Score</div>
            <div data-testid="detail-score" className="font-heading text-3xl font-bold text-ink">{attempt.score}/{attempt.total_questions}</div>
            <Trophy className="w-5 h-5 text-lemon mx-auto mt-1" />
          </div>
          <div className="p-5 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble">Accuracy</div>
            <div data-testid="detail-accuracy" className="font-heading text-3xl font-bold text-leaf">{attempt.accuracy}%</div>
            <Target className="w-5 h-5 text-leaf mx-auto mt-1" />
          </div>
          <div className="p-5 bg-white rounded-3xl card-tactile text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-pebble">Time</div>
            <div data-testid="detail-time" className="font-heading text-2xl font-bold text-sky">{fmtTime(attempt.time_taken_seconds)}</div>
            <Clock className="w-5 h-5 text-sky mx-auto mt-1" />
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold text-ink mb-4">Questions</h2>
        <div className="space-y-3" data-testid="detail-review-list">
          {review.map((r) => (
            <div key={r.question_id} className={`p-5 bg-white rounded-3xl card-tactile border-l-4 ${r.is_correct ? "border-leaf" : "border-watermelon"}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-pebble">Q{r.order}</div>
                {r.is_correct ? (
                  <span className="inline-flex items-center gap-1 text-leaf font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Correct</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-watermelon font-bold text-xs"><XCircle className="w-4 h-4" /> Incorrect</span>
                )}
              </div>
              <div className="font-heading text-lg font-semibold text-ink">
                {r.starting_number}
                {r.operations.map((op, j) => (
                  <span key={j}> <span className={op.op === "+" ? "text-leaf" : op.op === "-" ? "text-watermelon" : op.op === "×" ? "text-sky" : "text-sky-hover"}>{op.op}</span>{op.value}</span>
                ))}
                <span className="text-stone"> = {r.correct_answer}</span>
              </div>
              <div className="text-sm text-stone mt-2">Student answer: <span className={`font-bold ${r.is_correct ? "text-leaf" : "text-watermelon"}`}>{r.student_answer ?? "—"}</span></div>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
